import type { EntityRegistry } from './entityRegistry';
import type { EventBus } from './eventBus';
import type { Projectile, ProjectileType } from '../entities/projectile';
import type { Mine } from '../weapons/mine';
import type { Sheep } from '../weapons/sheep';
import type { MiniRaon } from '../weapons/raon';
import type { SuperSheep } from '../weapons/superSheep';
import type { HomingMissile } from '../weapons/homingMissile';
import type { Kamikaze } from '../weapons/kamikaze';
import type { NapalmDroplet } from '../entities/napalmDroplet';
import type { Crate } from '../entities/crate';
import type { OilBarrel } from '../entities/oilBarrel';
import { Fire } from '../entities/fire';
import type { Grave } from '../entities/grave';
import type { Worm } from '../entities/worm';
import { WA_PHYSICS } from './constants';
import { getGameRNG } from './rng';

export interface EntityFactoryCallbacks {
  createExplosion: (
    x: number,
    y: number,
    radius: number,
    damage: number,
    skipTerrain?: boolean,
    directHitWorm?: Worm,
    pushMultiplier?: number,
    magnet?: { radius: number; strength: number },
  ) => void;
  spawnFire: (x: number, y: number, count: number) => void;
  spawnFireAt: (x: number, y: number) => void;
  playSound: (soundId: string) => void;
  stopSound: (soundId: string) => void;
  handleCrateCollection: (crate: Crate, wormId: string) => void;
  destroyTerrain: (x: number, y: number, radius: number) => void;
  onWeaponFired: (endTurn: boolean) => void;
  showFloatingDamage: (x: number, y: number, damage: number) => void;
  getTeamColor: (teamId: string) => number;
  updateWeaponPanelAmmo: (team: unknown) => void;
  findWormById: (id: string) => Worm | undefined;
  getCurrentWaterLevel: () => number;
  isSolid: (x: number, y: number) => boolean;
  /** A projectile detonated at (x, y) (weather/Thor hooks; type for Aduka). */
  onProjectileExploded?: (x: number, y: number, type: ProjectileType, ownerId: string) => void;
}

export interface EntityFactoryDependencies {
  entityRegistry: EntityRegistry;
  eventBus: EventBus;
}

export interface AddEntityResult {
  shouldTrack: boolean;
  cameraMode?: string;
  entity: unknown;
}

export interface AddKamikazeResult extends AddEntityResult {
  teamColor: number;
}

const TRACKABLE_PROJECTILE_TYPES = [
  'grenade',
  'grub',
  'grubSS',
  'aduka',
  'nak',
  'adukaSS',
  'lightningT1',
  'lightningT2',
  'knight',
  'trico',
  'kalsiddon',
  'jdT2',
  'jdSS',
  'bazooka',
  'cluster',
  'dynamite',
  'holyGrenade',
  'banana',
  'mortar',
  'molotov',
];

/**
 * Per-shot-type explosion knockback multiplier (the shove a worm gets from a
 * direct/near contact explosion). Default is 0 (no shove) for any type not
 * listed. Classic explosives push full; the GunBound contact shots push a
 * little; the payload shots (aduka/lightning/knight/kalsiddon) push via their
 * strike/bolt/missile explosions instead. The mode applies a further global
 * `fire.explosionPushScale` on top (GunBound tones it down to 0.25).
 */
const EXPLOSION_PUSH_BY_TYPE: Partial<Record<string, number>> = {
  // Classic Worms-style explosives keep full knockback.
  grenade: 1,
  bazooka: 1,
  cluster: 1,
  clusterlet: 1,
  dynamite: 1,
  banana: 1,
  bananalet: 1,
  mortar: 1,
  molotov: 1,
  napalm: 1,
  holyGrenade: 1.8,
  grub: WA_PHYSICS.GRUB.PUSH_MULTIPLIER,
  // GunBound contact-exploding shots get a small base shove (the payload-based
  // ones - aduka/lightning/knight/kalsiddon - push via their strike/bolt/
  // missile explosions instead). The mode's explosionPushScale (0.25 in
  // GunBound) tones these down further, so they only nudge tanks.
  nak: 0.6,
  grubSS: 0.5,
  trico: 0.5,
};

/**
 * J.D.'s magnetic field per shot type: a wide pull/push applied SEPARATELY from
 * the (minimal) blast, over its own radius, weakening with distance. `strength`
 * is signed - NEGATIVE pulls worms toward the impact (T2 Magnet Sphere),
 * POSITIVE shoves them away (SS Anti-Magnet). See WA_PHYSICS.JD + ExplosionManager.applyMagnet.
 */
const MAGNET_BY_TYPE: Partial<Record<string, { radius: number; strength: number }>> = {
  jdT2: { radius: WA_PHYSICS.JD.T2_MAGNET_RADIUS, strength: WA_PHYSICS.JD.T2_MAGNET_FORCE },
  jdSS: { radius: WA_PHYSICS.JD.SS_MAGNET_RADIUS, strength: WA_PHYSICS.JD.SS_MAGNET_FORCE },
};

export class EntityFactory {
  private deps: EntityFactoryDependencies;
  private callbacks: EntityFactoryCallbacks;

  constructor(deps: EntityFactoryDependencies, callbacks: EntityFactoryCallbacks) {
    this.deps = deps;
    this.callbacks = callbacks;
  }

  addProjectile(projectile: Projectile): AddEntityResult {
    const customOnExplode = projectile.onExplode;
    projectile.onExplode = (x, y) => {
      // Tracer / sub-projectile weapons do NO on-contact explosion (no crater,
      // no contact damage) - only the follow-up payload (fired below via
      // customOnExplode / onProjectileExploded) deals damage.
      if (!projectile.noContactExplosion) {
        // Per-shot-type knockback. Unlisted types push 0 (no shove) by default;
        // the mode also globally scales this (GunBound tones it right down).
        const pushMultiplier = EXPLOSION_PUSH_BY_TYPE[projectile.type] ?? 0;
        this.callbacks.createExplosion(
          x,
          y,
          projectile.explosionRadius,
          projectile.maxDamage,
          projectile.skipTerrainOnExplode,
          undefined,
          pushMultiplier,
          MAGNET_BY_TYPE[projectile.type],
        );
      }
      customOnExplode?.(x, y);

      if (projectile.type === 'molotov') {
        this.callbacks.spawnFire(x, y, 5);
      }

      this.callbacks.onProjectileExploded?.(x, y, projectile.type, projectile.ownerId);
    };

    projectile.onDirectHit = (worm, x, y) => {
      if (projectile.noContactExplosion) return; // payload-only weapons: see onExplode
      const bonusDamage = Math.round(
        projectile.maxDamage * WA_PHYSICS.COLLISION.DIRECT_HIT_DAMAGE_MULTIPLIER,
      );
      this.callbacks.createExplosion(
        x,
        y,
        projectile.explosionRadius,
        bonusDamage,
        projectile.skipTerrainOnExplode,
        worm,
        EXPLOSION_PUSH_BY_TYPE[projectile.type] ?? 0,
        MAGNET_BY_TYPE[projectile.type],
      );
    };

    this.deps.entityRegistry.add('projectile', projectile);

    if (projectile.type === 'holyGrenade') {
      projectile.onPreExplode = () => {
        this.callbacks.playSound('holyGrenade');
      };
      projectile.onBounce = () => {
        this.callbacks.playSound('grenadeBounce');
      };
    } else if (
      projectile.type === 'grenade' ||
      projectile.type === 'cluster' ||
      projectile.type === 'grub'
    ) {
      projectile.onBounce = () => {
        this.callbacks.playSound('grenadeBounce');
      };
    } else if (projectile.type === 'banana' || projectile.type === 'bananalet') {
      projectile.onBounce = () => {
        this.callbacks.playSound('bananaBounce');
      };
    }

    const shouldTrack = TRACKABLE_PROJECTILE_TYPES.includes(projectile.type);
    return {
      shouldTrack,
      cameraMode: shouldTrack ? 'projectile' : undefined,
      entity: projectile,
    };
  }

  addHomingMissile(missile: HomingMissile): AddEntityResult {
    missile.onExplode = (x, y) => {
      this.callbacks.createExplosion(x, y, missile.explosionRadius, missile.maxDamage);
    };

    missile.onHomingActivated = () => {
      this.callbacks.playSound('rocketPowerUp');
    };

    this.deps.entityRegistry.add('homingMissile', missile);

    // Kalsiddon's apex missiles are sub-projectiles - the camera stays on the
    // tracer/bomb that spawned them (missile.track === false), not on them.
    const track = missile.track;
    return {
      shouldTrack: track,
      cameraMode: track ? 'homing' : undefined,
      entity: missile,
    };
  }

  addGrave(grave: Grave): AddEntityResult {
    grave.onExplode = (x, y) => {
      this.callbacks.createExplosion(x, y, grave.explosionRadius, grave.maxDamage);
    };

    this.deps.entityRegistry.add('grave', grave);

    return {
      shouldTrack: false,
      entity: grave,
    };
  }

  addMine(mine: Mine): AddEntityResult {
    mine.onExplode = (x, y) => {
      this.callbacks.createExplosion(x, y, mine.explosionRadius, mine.maxDamage);
    };

    mine.onArmed = () => {
      this.callbacks.playSound('mineArm');
    };

    mine.onTriggered = () => {
      this.callbacks.playSound('mineTick');
    };

    mine.onDud = () => {
      this.callbacks.playSound('mineDud');
    };

    this.deps.entityRegistry.add('mine', mine);

    return {
      shouldTrack: false,
      entity: mine,
    };
  }

  addRaon(raon: MiniRaon): AddEntityResult {
    raon.onExplode = (x, y) => {
      this.callbacks.createExplosion(x, y, raon.explosionRadius, raon.maxDamage);
    };
    raon.onArmed = () => {
      this.callbacks.playSound('mineArm');
    };

    this.deps.entityRegistry.add('raon', raon);

    return {
      shouldTrack: false,
      entity: raon,
    };
  }

  addSheep(sheep: Sheep): AddEntityResult {
    sheep.onExplode = (x, y) => {
      this.callbacks.createExplosion(x, y, sheep.explosionRadius, sheep.maxDamage);
    };

    // Frogs don't baa.
    if (sheep.kind !== 'frog') {
      sheep.onBaa = () => {
        this.callbacks.playSound('sheepBaa');
      };
      this.callbacks.playSound('sheepBaa');
    }

    this.deps.entityRegistry.add('sheep', sheep);

    return {
      shouldTrack: true,
      cameraMode: 'sheep',
      entity: sheep,
    };
  }

  addSuperSheep(superSheep: SuperSheep): AddEntityResult {
    superSheep.onExplode = (x, y) => {
      this.callbacks.createExplosion(x, y, superSheep.explosionRadius, superSheep.maxDamage);
      this.callbacks.stopSound('superSheepWhoosh');
    };

    superSheep.onBaa = () => {
      this.callbacks.playSound('sheepBaa');
    };

    superSheep.onFlyStart = () => {
      this.callbacks.playSound('superSheepRelease');
      this.callbacks.playSound('superSheepWhoosh');
    };

    superSheep.onFlyEnd = () => {
      this.callbacks.stopSound('superSheepWhoosh');
    };

    this.deps.entityRegistry.add('superSheep', superSheep);
    this.callbacks.playSound('sheepBaa');

    return {
      shouldTrack: true,
      cameraMode: 'supersheep',
      entity: superSheep,
    };
  }

  addKamikaze(kamikaze: Kamikaze, sourceWorm: Worm): AddKamikazeResult {
    sourceWorm.skipGraveOnDeath = true;

    kamikaze.onExplode = (x, y) => {
      this.callbacks.createExplosion(x, y, kamikaze.explosionRadius, kamikaze.maxDamage);
      sourceWorm.health = 0;
      sourceWorm.die();
    };

    kamikaze.onDrill = (x, y) => {
      this.callbacks.destroyTerrain(x, y, 12);
    };

    this.deps.entityRegistry.add('kamikaze', kamikaze);

    const teamColor = this.callbacks.getTeamColor(sourceWorm.teamId);

    return {
      shouldTrack: false,
      entity: kamikaze,
      teamColor,
    };
  }

  addNapalmDroplet(droplet: NapalmDroplet): AddEntityResult {
    droplet.onLand = (x, y) => {
      this.callbacks.spawnFireAt(x, y);
    };

    this.deps.entityRegistry.add('napalmDroplet', droplet);

    return {
      shouldTrack: false,
      entity: droplet,
    };
  }

  addCrate(crate: Crate): AddEntityResult {
    crate.onExplode = (x, y) => {
      this.callbacks.createExplosion(x, y, crate.explosionRadius, crate.maxDamage);
    };

    crate.onCollect = (wormId) => {
      this.callbacks.handleCrateCollection(crate, wormId);
    };

    this.deps.entityRegistry.add('crate', crate);

    return {
      shouldTrack: false,
      entity: crate,
    };
  }

  addBarrel(barrel: OilBarrel): AddEntityResult {
    barrel.onExplode = (x, y) => {
      this.callbacks.createExplosion(x, y, barrel.explosionRadius, barrel.maxDamage);
    };

    this.deps.entityRegistry.add('barrel', barrel);

    return {
      shouldTrack: false,
      entity: barrel,
    };
  }

  addFire(fire: Fire, onSpriteRemove?: (fireId: string) => void): AddEntityResult {
    fire.onSpread = (spreadX, spreadY) => {
      let groundY = spreadY;
      for (let j = 0; j < 30 && !this.callbacks.isSolid(spreadX, groundY + 1); j++) {
        groundY++;
      }
      if (groundY < WA_PHYSICS.WORLD.HEIGHT - this.callbacks.getCurrentWaterLevel()) {
        const newFire = new Fire({
          x: spreadX,
          y: groundY,
          duration: getGameRNG().nextFloat(4, 7),
          damagePerSecond: 5,
          spreadChance: 0.15,
          burnRadius: 3,
          burnInterval: 0.5,
        });
        this.addFire(newFire, onSpriteRemove);
      }
    };

    fire.onBurnTerrain = (bx, by, radius) => {
      const offsetX = getGameRNG().nextFloat(-radius * 1.5, radius * 1.5);
      const offsetY = getGameRNG().nextFloat(-radius * 0.5, radius * 1.5);
      this.callbacks.destroyTerrain(bx + offsetX, by + offsetY, radius);
    };

    fire.onExtinguish = () => {
      onSpriteRemove?.(fire.id);
    };

    this.deps.entityRegistry.add('fire', fire);

    return {
      shouldTrack: false,
      entity: fire,
    };
  }
}
